import { Component, Input, Output, EventEmitter, forwardRef, OnInit, ChangeDetectorRef, HostListener, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Overlay, OverlayRef, OverlayPositionBuilder } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ViewContainerRef, TemplateRef } from '@angular/core';

export interface TimeValue {
  hours: number;
  minutes: number;
  period?: 'AM' | 'PM';
}

@Component({
  selector: 'ws-time-input',
  templateUrl: './ws-time-input.component.html',
  styleUrls: ['./ws-time-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WsTimeInputComponent),
      multi: true
    }
  ]
})
export class WsTimeInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() is24HourFormat: boolean = true;
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() label: string = '';
  @Input() showLabel: boolean = true;
  @Input() minuteStep: number = 15; // Step for minutes (1, 5, 10, 15, etc.)
  @Input() autoInitializeWithCurrentTime: boolean = true; // Auto-initialize with nearest quick time to current time

  @Output() timeChange: EventEmitter<TimeValue | null> = new EventEmitter<TimeValue | null>();

  @ViewChild('timePopover', { static: true }) timePopover!: TemplateRef<any>;
  @ViewChild('hoursList', { static: false }) hoursList!: ElementRef;

  hours: number | null = null;
  minutes: number | null = null;
  period: 'AM' | 'PM' = 'AM';
  componentId: string = Math.random().toString(36).substr(2, 9);
  isDropdownOpen: boolean = false;
  
  // Keyboard navigation
  focusedColumn: 'hours' | 'minutes' | 'period' = 'hours';
  focusedIndex: { [key: string]: number } = { hours: 0, minutes: 0, period: 0 };

  private overlayRef: OverlayRef | null = null;
  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
    private cdr: ChangeDetectorRef, 
    private elementRef: ElementRef,
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit() {

  }

  ngOnDestroy() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }

  get hourOptions(): number[] {
    if (this.is24HourFormat) {
      return Array.from({length: 24}, (_, i) => i);
    } else {
      return Array.from({length: 12}, (_, i) => i + 1);
    }
  }

  get minuteOptions(): number[] {
    const options = [];
    for (let i = 0; i < 60; i += this.minuteStep) {
      options.push(i);
    }
    return options;
  }

  get periodOptions(): string[] {
    return ['AM', 'PM'];
  }

  onHourChange(hour: number | null) {
    this.hours = hour;
    this.emitTimeChange();
  }

  onMinuteChange(minute: number | null) {
    this.minutes = minute;
    this.emitTimeChange();
  }

  onPeriodChange(period: 'AM' | 'PM') {
    this.period = period;
    this.emitTimeChange();
  }

  // New dropdown methods
  toggleDropdown() {
    if (this.disabled) return;
    
    if (this.isDropdownOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    if (this.overlayRef || this.disabled) return;

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 4
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -4
        }
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    });

    const portal = new TemplatePortal(this.timePopover, this.viewContainerRef);
    this.overlayRef.attach(portal);
    this.isDropdownOpen = true;

    // Auto-initialize with current time if enabled and no time is set
    if (this.autoInitializeWithCurrentTime && this.hours === null && this.minutes === null) {
      this.initializeWithCurrentTime();
    }

    // Set initial focus
    this.resetKeyboardNavigation();

    // Close on backdrop click
    this.overlayRef.backdropClick().subscribe(() => {
      this.closeDropdown();
    });

    // Scroll to current hour after the view is rendered
    setTimeout(() => {
      this.scrollToCurrentHour();
    }, 50);
  }

  private scrollToCurrentHour() {
    if (this.hoursList && this.hours !== null) {
      const hourIndex = this.hourOptions.indexOf(this.hours);
      if (hourIndex >= 0) {
        const hourButtons = this.hoursList.nativeElement.querySelectorAll('.time-option');
        if (hourButtons[hourIndex]) {
          hourButtons[hourIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }
    }
  }

  closeDropdown() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.isDropdownOpen = false;
    this.onTouched();
  }

  selectHour(hour: number) {
    this.hours = hour;
    this.emitTimeChange();
    // Don't auto-close when selecting hour - let user select minutes too
  }

  selectMinute(minute: number) {
    this.minutes = minute;
    this.emitTimeChange();
    this.checkAndCloseIfComplete();
  }

  selectPeriod(period: 'AM' | 'PM') {
    this.period = period;
    this.emitTimeChange();
    this.checkAndCloseIfComplete();
  }

  private checkAndCloseIfComplete() {
    // Close popover if we have both hours and minutes selected
    if (this.hours !== null && this.minutes !== null) {
      setTimeout(() => {
        this.closeDropdown();
      }, 150); // Small delay for visual feedback
    }
  }

  onMinuteInputBlur(event: Event) {
    const target = event.target as HTMLInputElement;
    let value = parseInt(target.value, 10);
    
    // Validate minute range
    if (isNaN(value) || value < 0) {
      this.minutes = null;
      target.value = '';
    } else if (value > 59) {
      value = 59;
      target.value = '59';
      this.minutes = value;
    } else {
      this.minutes = value;
    }
    
    this.emitTimeChange();
    this.checkAndCloseIfComplete();
  }

  onHourInputBlur(event: Event) {
    const target = event.target as HTMLInputElement;
    let value = parseInt(target.value, 10);
    
    // Validate hour range based on format
    const minHour = this.is24HourFormat ? 0 : 1;
    const maxHour = this.is24HourFormat ? 23 : 12;
    
    if (isNaN(value) || value < minHour) {
      this.hours = null;
      target.value = '';
    } else if (value > maxHour) {
      value = maxHour;
      target.value = maxHour.toString();
      this.hours = value;
    } else {
      this.hours = value;
    }
    
    this.emitTimeChange();
  }

  onMinuteInputKeydown(event: KeyboardEvent) {
    // Allow navigation and special keys
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape', 'Backspace', 'Delete'].includes(event.key)) {
      if (event.key === 'Enter') {
        event.preventDefault();
        // Trigger blur validation when Enter is pressed
        const target = event.target as HTMLInputElement;
        target.blur();
        this.closeDropdown();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDropdown();
      }
      return;
    }
    
    // Only allow numbers
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }

  onHourInputKeydown(event: KeyboardEvent) {
    // Allow navigation and special keys
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape', 'Backspace', 'Delete'].includes(event.key)) {
      if (event.key === 'Enter') {
        event.preventDefault();
        // Trigger blur validation when Enter is pressed
        const target = event.target as HTMLInputElement;
        target.blur();
        this.closeDropdown();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDropdown();
      }
      return;
    }
    
    // Only allow numbers
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleDropdown();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDropdown();
    } else if (this.isDropdownOpen) {
      this.handlePopoverKeydown(event);
    }
  }

  handlePopoverKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.navigateUp();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.navigateDown();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.navigateLeft();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.navigateRight();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectCurrentFocused();
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  // Keyboard navigation methods
  resetKeyboardNavigation() {
    this.focusedColumn = 'hours';
    this.focusedIndex = { hours: 0, minutes: 0, period: 0 };
    
    // Set initial focused indices based on current values
    if (this.hours !== null) {
      this.focusedIndex.hours = this.hourOptions.indexOf(this.hours);
    }
    if (this.minutes !== null) {
      this.focusedIndex.minutes = this.minuteOptions.indexOf(this.minutes);
    }
    if (!this.is24HourFormat) {
      this.focusedIndex.period = this.periodOptions.indexOf(this.period);
    }
  }

  navigateUp() {
    const options = this.getCurrentOptions();
    if (this.focusedIndex[this.focusedColumn] > 0) {
      this.focusedIndex[this.focusedColumn]--;
    }
  }

  navigateDown() {
    const options = this.getCurrentOptions();
    if (this.focusedIndex[this.focusedColumn] < options.length - 1) {
      this.focusedIndex[this.focusedColumn]++;
    }
  }

  navigateLeft() {
    if (this.focusedColumn === 'minutes') {
      this.focusedColumn = 'hours';
    } else if (this.focusedColumn === 'period' && !this.is24HourFormat) {
      this.focusedColumn = 'minutes';
    }
  }

  navigateRight() {
    if (this.focusedColumn === 'hours') {
      this.focusedColumn = 'minutes';
    } else if (this.focusedColumn === 'minutes' && !this.is24HourFormat) {
      this.focusedColumn = 'period';
    }
  }

  getCurrentOptions(): any[] {
    switch (this.focusedColumn) {
      case 'hours': return this.hourOptions;
      case 'minutes': return this.minuteOptions;
      case 'period': return this.periodOptions;
      default: return [];
    }
  }

  selectCurrentFocused() {
    const options = this.getCurrentOptions();
    const selectedValue = options[this.focusedIndex[this.focusedColumn]];
    
    switch (this.focusedColumn) {
      case 'hours':
        this.selectHour(selectedValue);
        break;
      case 'minutes':
        this.selectMinute(selectedValue);
        break;
      case 'period':
        this.selectPeriod(selectedValue);
        break;
    }
  }

  isOptionFocused(column: string, index: number): boolean {
    return this.focusedColumn === column && this.focusedIndex[column] === index;
  }

  getSelectValue(event: Event): number | null {
    const target = event.target as HTMLSelectElement;
    const value = target.value;
    return value === 'null' || value === '' ? null : +value;
  }

  getPeriodValue(event: Event): 'AM' | 'PM' {
    const target = event.target as HTMLSelectElement;
    return target.value as 'AM' | 'PM';
  }

  private emitTimeChange() {
    const timeValue = this.getTimeValue();
    this.timeChange.emit(timeValue);
    this.onChange(timeValue);
    this.onTouched();
  }

  private getTimeValue(): TimeValue | null {
    // Allow partial time values (only hours selected)
    if (this.hours === null) {
      return null;
    }

    return {
      hours: this.hours,
      minutes: this.minutes ?? 0,
      ...(this.is24HourFormat ? {} : { period: this.period })
    };
  }

  // ControlValueAccessor implementation
  writeValue(value: TimeValue | null): void {
    if (value) {
      this.hours = value.hours;
      this.minutes = value.minutes;
      if (!this.is24HourFormat && value.period) {
        this.period = value.period;
      }
    } else {
      this.hours = null;
      this.minutes = null;
      this.period = 'AM';
    }
    this.cdr.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }

  // Format display values
  formatHour(hour: number | null): string {
    if (hour === null || hour === undefined) {
      return '';
    }
    if (this.is24HourFormat) {
      return hour.toString().padStart(2, '0');
    }
    return hour.toString();
  }

  formatMinute(minute: number | null): string {
    if (minute === null || minute === undefined) {
      return '';
    }
    return minute.toString().padStart(2, '0');
  }

  // Get current time as string for display
  get displayTime(): string {
    if (this.hours === null || this.minutes === null) {
      return this.placeholder || '';
    }

    const hourDisplay = this.formatHour(this.hours);
    const minuteDisplay = this.formatMinute(this.minutes);
    const periodDisplay = this.is24HourFormat ? '' : ` ${this.period}`;

    return `${hourDisplay}:${minuteDisplay}${periodDisplay}`;
  }

  // Clear the time selection
  clearTime() {
    this.hours = null;
    this.minutes = null;
    this.period = 'AM';
    this.emitTimeChange();
    this.closeDropdown();
  }

  // Initialize with nearest quick time to current time
  private initializeWithCurrentTime() {
    const now = new Date();
    let currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // Find the next minute option based on minuteStep
    const nextMinute = this.findNearestMinuteOption(currentMinute);
    
    // If we wrapped to the first option of next hour, advance the hour
    if (nextMinute < currentMinute) {
      currentHour += 1;
      // Handle 24-hour wrap around
      if (currentHour >= 24) {
        currentHour = 0;
      }
    }

    // Convert to 12-hour format if needed
    if (!this.is24HourFormat) {
      this.period = currentHour >= 12 ? 'PM' : 'AM';
      if (currentHour === 0) {
        currentHour = 12;
      } else if (currentHour > 12) {
        currentHour = currentHour - 12;
      }
    }

    this.hours = currentHour;
    this.minutes = nextMinute;
    this.emitTimeChange();
  }

  // Find the next available minute option based on the minuteStep
  private findNearestMinuteOption(currentMinute: number): number {
    const minuteOptions = this.minuteOptions;
    
    // If minuteStep is 1, return the exact minute
    if (this.minuteStep === 1) {
      return currentMinute;
    }

    // Find the next closest option (round up)
    for (const option of minuteOptions) {
      if (option >= currentMinute) {
        return option;
      }
    }

    // If no option is greater than current minute, wrap to the first option of next hour
    return minuteOptions[0];
  }
}